import { Box, Button, Flex, IconButton, Text } from '@chakra-ui/react';

import { isDefined } from '@enterprise/core';
import { Bell, BellSlash, DefaultToolTip } from '@enterprise/ui';

import type { NotificationPanelProps } from './types/notification-panel.props';

import { NotificationEmpty } from './components/notification-empty/notification-empty';
import { NotificationRow } from './components/notification-row/notification-row';
import { NotificationTabs } from './components/notification-tabs/notification-tabs';
import { useNotificationPanelConfiguration } from './hooks/notification-panel-configuration.hook';

export const NotificationPanel = ({
  freshId,
  nativePermission,
  notifications,
  onDelete,
  onDeleteAll,
  onOpen,
  onReadAll,
  onRequestNativePermission,
}: NotificationPanelProps) => {
  const { activeTab, counts, filtered, hasUnread, isDeleteAllDisabled, isPanelEmpty, isReadAllDisabled, onTabChange } =
    useNotificationPanelConfiguration(notifications);

  const renderNativeToggle = () => {
    if (!isDefined(onRequestNativePermission) || nativePermission === 'unsupported') {
      return null;
    }

    const isGranted = nativePermission === 'granted';
    const label = isGranted ? 'Browser notifications on' : 'Enable browser notifications';

    return (
      <DefaultToolTip label={label}>
        <IconButton
          aria-label={label}
          icon={isGranted ? <Bell /> : <BellSlash />}
          onClick={onRequestNativePermission}
          variant="ghost"
        />
      </DefaultToolTip>
    );
  };

  const renderUnreadBadge = () => {
    if (!hasUnread) {
      return null;
    }

    return (
      <Box
        bg="hsp.500"
        borderRadius="full"
        color="white"
        fontSize="11px"
        fontWeight="bold"
        minW="20px"
        ml={2}
        px="8px"
        textAlign="center">
        {counts.unread}
      </Box>
    );
  };

  const renderRows = () => {
    if (isPanelEmpty) {
      return <NotificationEmpty tab={activeTab} />;
    }

    return filtered.map((notification) => {
      return (
        <NotificationRow
          isFresh={notification.id === freshId}
          key={notification.id}
          notification={notification}
          onDelete={onDelete}
          onOpen={onOpen}
        />
      );
    });
  };

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="2xl"
      boxShadow="lg"
      maxW="calc(100vw - 24px)"
      overflow="hidden"
      position="absolute"
      right={0}
      top="calc(100% + 6px)"
      w="420px"
      zIndex="popover">
      {/* Header */}
      <Flex
        align="center"
        bg="gray.50"
        borderBottom="1px solid"
        borderColor="gray.100"
        p="4">
        <Text
          color="gray.800"
          fontSize="19px"
          fontWeight="bold">
          Activity
        </Text>
        {renderUnreadBadge()}
        <Box flex={1} />
        {renderNativeToggle()}
      </Flex>

      {/* Tabs */}
      <NotificationTabs
        activeTab={activeTab}
        counts={counts}
        onChange={onTabChange}
      />

      {/* Rows */}
      <Box
        maxH="400px"
        overflowY="auto"
        role="list">
        {renderRows()}
      </Box>

      {/* Footer */}
      <Flex
        bg="gray.50"
        borderColor="gray.100"
        borderTop="1px solid"
        gap={2}
        justify="space-between"
        p={4}>
        <Button
          colorScheme="red"
          isDisabled={isDeleteAllDisabled}
          onClick={onDeleteAll}
          variant="ghost">
          Delete All
        </Button>
        <Button
          colorScheme="brand"
          isDisabled={isReadAllDisabled}
          onClick={onReadAll}
          variant="ghost">
          Mark All as read
        </Button>
      </Flex>
    </Box>
  );
};
